(() => {
  'use strict';

  window.LimitlessMotion?.destroy?.();

  // Content is always visible in CSS. Animations only decorate a visible state.
  const revealSelector = '[data-ll-reveal], [data-ll-motion="reveal"], [data-ll-motion="project"]';
  const orbitSelector = '.ll-orbit b, .ll-process__orbit b, [data-ll-motion="orbit"]';
  const selector = `${revealSelector}, ${orbitSelector}`;
  const projectSelector = '.ll-work-item, .ll-project, .ll-case__gallery-item, [data-ll-motion="project"]';
  const mediaSelector = '[data-ll-motion-image], .ll-work-item__image, .ll-project__media, .ll-case__gallery-media, .ll-experience-panel__media';
  const copySelector = '[data-ll-motion-copy], .ll-work-item__caption, .ll-project__body, .ll-experience-panel__copy, figcaption';
  const panelSelector = '.ll-experience-panel';
  const excludedSelector = '[data-ll-motion="none"], [data-ll-no-motion], .ll-header, .ll-header-section, .ll-hero__copy, .ll-cta, .ll-quote, form';
  const hiddenSelector = '[hidden], [inert], [role="tabpanel"][aria-hidden="true"]';
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const active = new Map();
  const observed = new Set();
  const seen = new WeakSet();
  const events = [];
  let observer;
  let destroyed = false;

  const inEditor = () => Boolean(window.Shopify?.designMode);
  const canAnimate = () => !destroyed && !document.hidden && !inEditor() && Boolean(reducedMotion) && !reducedMotion.matches && typeof Element.prototype.animate === 'function';
  const contains = (root, element) => root === document || root === element || Boolean(root.contains?.(element));
  const elementsWithin = (root) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...root.querySelectorAll(selector),
  ];
  const wasSeen = (element) => seen.has(element) || element.classList.contains('ll-is-visible');
  const isHidden = (element) => Boolean(element.closest(hiddenSelector));
  const excluded = (element) => Boolean(element.closest(excludedSelector) || element.querySelector('form') || element.matches('a, button, input, select, textarea, summary'));

  const cancel = (element) => {
    const record = active.get(element);
    if (!record) return;
    active.delete(element);
    record.animation.onfinish = null;
    record.animation.oncancel = null;
    record.animation.cancel();
  };

  const stop = (root = document) => {
    observed.forEach((element) => {
      if (!contains(root, element)) return;
      observer?.unobserve(element);
      observed.delete(element);
    });
    [...active].forEach(([element, record]) => {
      if (contains(root, element) || contains(root, record.owner)) cancel(element);
    });
    if (!observed.size) {
      observer?.disconnect();
      observer = undefined;
    }
  };

  const animate = (element, owner, frames, duration) => {
    if (!element || !canAnimate() || !element.isConnected || isHidden(element) || owner.matches(':focus-within')) return;
    cancel(element);
    try {
      const animation = element.animate(frames, {
        duration,
        easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
        iterations: 1,
        fill: 'none',
      });
      active.set(element, { animation, owner });
      const complete = () => {
        if (active.get(element)?.animation === animation) active.delete(element);
        animation.onfinish = null;
        animation.oncancel = null;
      };
      animation.onfinish = complete;
      animation.oncancel = complete;
    } catch (_) {
      active.delete(element);
    }
  };

  const projectEntrance = (owner, panel = false) => {
    const media = owner.querySelector(mediaSelector);
    const copy = owner.querySelector(copySelector);
    if (!media && !copy) {
      animate(owner, owner, [{ opacity: 0.78, transform: 'translateY(12px)' }, { opacity: 1, transform: 'translateY(0)' }], panel ? 360 : 520);
      return;
    }
    // Animate the clipping frame, not its image: image hover remains independent.
    animate(media, owner, [{ opacity: 0.78, transform: `translateY(${panel ? 6 : 12}px)` }, { opacity: 1, transform: 'translateY(0)' }], panel ? 360 : 620);
    if (copy && !media?.contains(copy)) {
      animate(copy, owner, [{ opacity: 0.8, transform: `translateY(${panel ? 4 : 8}px)` }, { opacity: 1, transform: 'translateY(0)' }], panel ? 300 : 420);
    }
  };

  const show = (element, shouldAnimate = true) => {
    observer?.unobserve(element);
    observed.delete(element);
    if (wasSeen(element)) return;
    seen.add(element);
    element.classList.remove('ll-will-reveal');
    element.classList.add('ll-is-visible');
    if (!shouldAnimate || !canAnimate() || !element.isConnected || isHidden(element) || element.matches(':focus-within')) return;

    if (element.matches(orbitSelector)) {
      animate(element, element, [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], 2600);
    } else if (element.matches(projectSelector)) {
      projectEntrance(element);
    } else {
      animate(element, element, [{ opacity: 0.45, transform: 'translateY(16px)' }, { opacity: 1, transform: 'translateY(0)' }], 520);
    }
  };

  const init = (root = document) => {
    if (destroyed || !root.querySelectorAll) return;
    document.documentElement.classList.remove('ll-motion');
    document.documentElement.classList.toggle('ll-motion-editor', inEditor());
    if (inEditor()) stop();
    const enabled = canAnimate() && typeof window.IntersectionObserver === 'function';
    const candidates = elementsWithin(root).filter((element) => !wasSeen(element) && !observed.has(element));
    const measurements = candidates.map((element) => ({ element, rect: enabled ? element.getBoundingClientRect() : null }));

    measurements.forEach(({ element, rect }) => {
      if (excluded(element)) {
        show(element, false);
        return;
      }
      if (!enabled) {
        show(element, false);
        return;
      }
      // Hidden tab panels are left alone until the component shows them.
      if (isHidden(element) || rect.width === 0 || rect.height === 0) return;
      if (rect.bottom <= 0 || (!element.matches(orbitSelector) && rect.top < window.innerHeight)) {
        show(element, false);
        return;
      }
      try {
        if (!observer) {
          observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              // A queued callback may arrive after unload, focus or destroy.
              if (!destroyed && observed.has(entry.target) && entry.isIntersecting && !isHidden(entry.target)) show(entry.target);
            });
          }, { threshold: 0, rootMargin: '0px 0px 4% 0px' });
        }
        observed.add(element);
        observer.observe(element);
      } catch (_) {
        show(element, false);
      }
    });
  };

  const on = (target, type, listener) => {
    target.addEventListener(type, listener);
    events.push(() => target.removeEventListener(type, listener));
  };

  const makeStable = (root) => {
    document.documentElement.classList.toggle('ll-motion-editor', inEditor());
    stop(root);
    elementsWithin(root).forEach((element) => show(element, false));
  };

  const preferenceChanged = () => {
    if (reducedMotion.matches) makeStable(document);
    else init();
  };

  on(document, 'shopify:section:load', (event) => init(event.target));
  on(document, 'shopify:section:unload', (event) => stop(event.target));
  on(document, 'shopify:section:select', (event) => makeStable(event.target));
  on(document, 'shopify:block:select', (event) => makeStable(event.target));
  on(document, 'shopify:inspector:activate', () => makeStable(document));
  on(document, 'll:panel:change', (event) => {
    const panel = event.target.closest?.(panelSelector);
    if (!panel || !panel.isConnected || isHidden(panel)) return;
    makeStable(panel.closest('[data-ll-experience]') || panel.closest('.ll-experience-tabs') || panel);
    if (event.detail?.animate !== false && !excluded(panel)) projectEntrance(panel, true);
  });
  on(document, 'focusin', (event) => {
    [...active].forEach(([element, record]) => {
      // Focus in the caption must also stop the sibling media animation.
      if (contains(record.owner, event.target) || contains(element, event.target)) cancel(element);
    });
    let parent = event.target;
    while (parent?.closest) {
      const target = parent.closest(selector);
      if (!target) break;
      show(target, false);
      parent = target.parentElement;
    }
  });
  on(document, 'pointerdown', (event) => {
    [...active].forEach(([element, record]) => {
      if (contains(record.owner, event.target)) cancel(element);
    });
  });
  on(document, 'visibilitychange', () => {
    if (document.hidden) [...active.keys()].forEach(cancel);
  });
  on(window, 'pagehide', () => stop());
  on(window, 'pageshow', (event) => {
    if (event.persisted) init();
  });

  if (reducedMotion?.addEventListener) on(reducedMotion, 'change', preferenceChanged);
  else if (reducedMotion?.addListener) {
    reducedMotion.addListener(preferenceChanged);
    events.push(() => reducedMotion.removeListener(preferenceChanged));
  }

  window.LimitlessMotion = {
    init,
    destroy() {
      if (destroyed) return;
      stop();
      events.forEach((remove) => remove());
      destroyed = true;
    },
  };

  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', () => init());
  else init();
})();
